import { FC, useState } from "react";
import { useAppDispatch } from "../../../custom-hooks/useAppDispatch";
import { ApplicationServiceImpl } from "../../../services/ApplicationServiceImpl";
import { ApplicationDTO } from "../../../domain/models/dto/ApplicationDTO";
import DeclineCrossSVG from "../svg/functional/DeclineCrossSVG";
import AcceptCheckMarkSVG from "../svg/functional/AcceptCheckMarkSVG";
import { isApplicationAccepted, isApplicationDeclined, isWorkersHired } from "../../../slices/works-slice";
import { useTranslation } from "react-i18next";
import { WorkDTO } from "../../../domain/models/dto/WorkDTO";
import useScreenSize from "../../../custom-hooks/base-hooks/useScreenSize";
import { WorkServiceImpl } from "../../../services/WorkServiceImpl";

interface AcceptAndDeclineButtonsProps {
    application: ApplicationDTO;
    work: WorkDTO;
    isAcceptOrDeclineAvailable: boolean;
    setAcceptOrDeclineAvailable: (value: boolean) => void;
}

const AcceptAndDeclineButtons: FC<AcceptAndDeclineButtonsProps> = (props) => {
    const [isRestrictionVisible, setRestrictionVisible] = useState(false);
    const { upTo650, moreOrEqual650 } = useScreenSize();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const isAccepted = isApplicationAccepted(props.application);
    const isDeclined = isApplicationDeclined(props.application);
    const isHired = isWorkersHired(props.work);
    const refreshWorkAfterApplicationTransition = async (
        transition: Promise<{ meta: { requestStatus: string } }>
    ) => {
        const result = await transition;
        if (result.meta.requestStatus === "fulfilled") {
            await dispatch(
                WorkServiceImpl.getWorkByIdThunk({ id: props.work.id })
            );
        }
    };
    const approveHandler = () => {
        if (isHired && !isAccepted) {
            setRestrictionVisible(true);
            return props.setAcceptOrDeclineAvailable(true);
        }
        return (
            !isAccepted &&
            refreshWorkAfterApplicationTransition(
                dispatch(
                    ApplicationServiceImpl.approveApplicationThunk({
                        id: props.application.id,
                    })
                )
            )
        );
    };
    const declineHandler = () => {
        setRestrictionVisible(false);
        props.setAcceptOrDeclineAvailable(false);
        return (
            !isDeclined &&
            refreshWorkAfterApplicationTransition(
                dispatch(
                    ApplicationServiceImpl.declineApplicationThunk({
                        id: props.application.id,
                    })
                )
            )
        );
    };

    return (
        <div className="relative flex items-center justify-center mt-4 dmax2:mt-5 mb-8 dmax2:mb-9">
            <div
                onClick={declineHandler}
                className={`flex items-center justify-center w-[52px] dmax2:w-11 h-[52px] dmax2:h-11 bg-accentRed hover:bg-accentRedFocused cursor-pointer ${
                    isAccepted && "bg-textFaded hover:bg-accentRedFocused"
                } rounded-full transition duration-300 mr-8 dmax2:mr-5`}
            >
                <DeclineCrossSVG className="text-textWhite" />
            </div>
            <div
                onClick={approveHandler}
                className={`flex items-center justify-center w-[52px] dmax2:w-11 h-[52px] dmax2:h-11 bg-accentGreen hover:bg-accentGreenFocused cursor-pointer ${
                    isDeclined && "bg-textFaded hover:bg-accentGreenFocused"
                } rounded-full transition duration-300 tmin4:mr-8`}
            >
                <AcceptCheckMarkSVG className="text-textWhite" />
            </div>
            {upTo650 && props.isAcceptOrDeclineAvailable && isRestrictionVisible && (
                <div className="absolute H14 dmax2:H12 text-textWhite w-[239px] dmax2:w-[190px] bg-backgroundInput rounded-lg py-3 dmax2:py-2 px-4 dmax2:px-3">
                    {t("work.workersAmountRestriction", { workersAmount: props.work.workersAmount })}
                </div>
            )}
            {moreOrEqual650 && props.isAcceptOrDeclineAvailable && isRestrictionVisible && (
                <div className="relative">
                    <div className="absolute top-[-22px] H14 dmax2:H12 text-textWhite w-[239px] dmax2:w-[190px] bg-backgroundInput rounded-lg py-3 dmax2:py-2 px-4 dmax2:px-3">
                        {t("work.workersAmountRestriction", { workersAmount: props.work.workersAmount })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AcceptAndDeclineButtons;
